import { usePostHog } from 'posthog-js/react'
import { api } from '../../api/client'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'

interface PathStepProps {
    onSkip: (step: number) => void
}

interface PathOption {
    id: 'Import' | 'AddExpense' | 'Explore'
    emoji: string
    label: string
    subtext: string
    destination: string
    isHero?: boolean
}

const PATHS: PathOption[] = [
    {
        id: 'Import',
        emoji: '📊',
        label: 'I have a spreadsheet',
        subtext: 'Import your historical expenses in 2 minutes',
        destination: '/expenses/import',
        isHero: true,
    },
    {
        id: 'AddExpense',
        emoji: '➕',
        label: 'Start fresh',
        subtext: 'Add your first expense manually',
        destination: '/expenses',
    },
    {
        id: 'Explore',
        emoji: '👀',
        label: 'Just exploring',
        subtext: 'See a sample expense and explore the app',
        destination: '/dashboard',
    },
]

export default function PathStep({ onSkip }: PathStepProps) {
    const posthog = usePostHog()
    const navigate = useNavigate()

    const handleChoosePath = async (option: PathOption) => {
        try {
            await api.updateUserProfile({
                pathChosen: option.id,
                onboardingCompleted: true,
            })

            posthog.capture('onboarding_path_chosen', {
                path: option.id === 'Import' ? 'import' : option.id === 'AddExpense' ? 'add_expense' : 'explore',
            })

            // Seed demo data for the "Just exploring" path
            if (option.id === 'Explore') {
                try {
                    await api.seedDemoData()
                } catch {
                    // Non-critical — proceed even if seeding fails
                }
            }

            navigate(option.destination)
        } catch {
            toast.error('Something went wrong. Please try again.')
        }
    }

    return (
        <div className="path-step">
            <div className="wizard-header">
                <span className="wizard-step-label">Step 3 of 3</span>
                <h2 className="wizard-title">How do you want to start?</h2>
                <p className="wizard-subtitle">Choose the option that best describes where you are right now.</p>
            </div>

            <div className="path-cards" role="list">
                {PATHS.map((option) => (
                    <button
                        key={option.id}
                        className={`path-card${option.isHero ? ' path-card--hero' : ''}`}
                        onClick={() => handleChoosePath(option)}
                        id={`path-${option.id.toLowerCase()}`}
                        role="listitem"
                        type="button"
                    >
                        <span className="path-card-emoji" aria-hidden="true">{option.emoji}</span>
                        <div className="path-card-content">
                            <div className="path-card-label">{option.label}</div>
                            <div className="path-card-subtext">{option.subtext}</div>
                        </div>
                        {option.isHero && (
                            <span className="path-card-badge">Most popular</span>
                        )}
                    </button>
                ))}
            </div>

            <div className="wizard-actions wizard-actions--centered">
                <button
                    className="btn-link skip-link"
                    onClick={() => onSkip(3)}
                    id="path-step-skip"
                    type="button"
                >
                    Skip setup
                </button>
            </div>
        </div>
    )
}
